import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function HowItWorksSection() {
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: imagesRef, isVisible: imagesVisible } = useScrollAnimation({ threshold: 0.2 });

  const steps = [
    {
      number: '01',
      icon: 'ri-file-list-3-line',
      title: 'Lease Equipment',
      description: 'Select your imaging modality and lease term. We offer flexible short-term and long-term contracts with transparent pricing.',
      tag: 'You choose the equipment'
    },
    {
      number: '02',
      icon: 'ri-truck-line',
      title: 'We Deliver &amp; Install',
      description: 'Our logistics team delivers, positions, and connects the equipment at your facility. We handle power, placement, and site coordination.',
      tag: 'We handle all logistics'
    },
    {
      number: '03',
      icon: 'ri-hospital-line',
      title: 'Your Team Operates',
      description: 'Your licensed clinical staff operates the equipment. All imaging services, scheduling, patient care, and billing remain entirely under your control.',
      tag: 'Your team runs the operation'
    },
    {
      number: '04',
      icon: 'ri-tools-line',
      title: 'We Maintain Equipment',
      description: 'We provide ongoing preventative maintenance, uptime monitoring, and technical servicing of the equipment throughout the lease term.',
      tag: 'We keep equipment running'
    }
  ];

  return (
    <section className="py-16 sm:py-24 lg:py-40 px-4 sm:px-6 bg-[#F7F9FB] overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          {/* Left Content Column */}
          <div className="lg:col-span-3">
            <div
              ref={contentRef}
              className={`transition-all duration-700 ease-out ${
                contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3 sm:mb-4">
                HOW IT WORKS
              </p>
              <h2 className="text-navy text-[28px] sm:text-[34px] lg:text-[40px] font-semibold leading-tight mb-4 sm:mb-6">
                Simple. Clear. Equipment-Focused.
              </h2>
              <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed mb-8 sm:mb-12">
                We are an equipment lessor and infrastructure provider. We do not provide clinical services, staffing, or operational management. Your facility retains full control of all imaging operations.
              </p>
            </div>

            {/* Steps */}
            <div ref={stepsRef} className="space-y-6 sm:space-y-8">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-4 sm:gap-6 transition-all duration-500 ease-out ${
                    stepsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                  }`}
                  style={{ transitionDelay: stepsVisible ? `${index * 120}ms` : '0ms' }}
                >
                  <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 bg-navy rounded-2xl flex flex-col items-center justify-center">
                    <span className="text-white/50 text-[10px] font-bold leading-none">{step.number}</span>
                    <i className={`${step.icon} text-white text-xl mt-0.5`}></i>
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="flex flex-wrap items-center gap-3 mb-1.5">
                      <h3 className="text-navy text-[16px] sm:text-[18px] font-semibold" dangerouslySetInnerHTML={{ __html: step.title }}></h3>
                      <span className="bg-slate-blue/10 text-slate-blue text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap">
                        {step.tag}
                      </span>
                    </div>
                    <p className="text-[#5A6C7D] text-sm sm:text-[15px] leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Disclaimer Box */}
            <div
              className={`mt-8 sm:mt-10 p-5 sm:p-6 bg-navy/5 border-l-4 border-navy rounded-r-xl transition-all duration-700 ease-out delay-500 ${
                stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <h4 className="text-navy text-[14px] font-semibold mb-2 uppercase tracking-wide">Operational Responsibility</h4>
              <p className="text-[#5A6C7D] text-sm leading-relaxed">
                The Company provides medical imaging equipment only. All clinical services — including staffing, scheduling, patient care, billing, and compliance — are the sole responsibility of the healthcare provider. The Company does not provide radiology services, technologists, or clinical personnel.
              </p>
            </div>
          </div>

          {/* Right Visual Column */}
          <div ref={imagesRef} className="lg:col-span-2">
            <div className="space-y-4">
              <div
                className={`rounded-2xl sm:rounded-3xl overflow-hidden h-[240px] sm:h-[300px] lg:h-[360px] transition-all duration-700 ease-out ${
                  imagesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'
                }`}
              >
                <img
                  src="https://public.readdy.ai/ai/img_res/f3e006a05151fb9878475028d768b7c4.jpg"
                  alt="Equipment delivery and setup"
                  className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
                />
              </div>
              <div
                className={`rounded-2xl sm:rounded-3xl overflow-hidden h-[180px] sm:h-[200px] lg:h-[240px] transition-all duration-700 ease-out delay-200 ${
                  imagesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'
                }`}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Close-up%20view%20of%20professional%20power%20connection%20and%20electrical%20infrastructure%20setup%20for%20mobile%20medical%20imaging%20equipment%20trailer%20showing%20technical%20installation%20with%20cables%20and%20power%20supply%20components%20in%20clean%20industrial%20setting%20with%20neutral%20background&width=800&height=480&seq=how-it-works-power-001&orientation=landscape"
                  alt="Power and infrastructure setup"
                  className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
